import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Badge,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as VerifiedIcon,
  Schedule as TimerIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Block as BlockIcon,
  Visibility as ViewIcon,
  Shield as ShieldIcon,
  AccessTime as AccessTimeIcon,
  DriveEta as DriverIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface AccessCode {
  id: string;
  code: string;
  type: 'vehicle_pickup' | 'driver_verification' | 'renter_verification';
  bookingId: string;
  generatedFor: {
    id: string;
    name: string;
    role: 'renter' | 'driver' | 'host';
    phone: string;
  };
  relatedTo?: {
    id: string;
    name: string;
    role: 'renter' | 'driver' | 'host';
  };
  vehicleInfo?: {
    make: string;
    model: string;
    plate: string;
  };
  status: 'active' | 'used' | 'expired' | 'revoked';
  generatedAt: string;
  expiresAt: string;
  usedAt?: string;
  verificationAttempts: number;
  maxAttempts: number;
  securityEvents: Array<{
    type: 'generated' | 'attempted' | 'used' | 'failed' | 'revoked';
    timestamp: string;
    details: string;
  }>;
}

interface CodeSettings {
  generateBeforeMinutes: number;
  expiryAfterMinutes: number;
  maxVerificationAttempts: number;
  enableAutoGeneration: boolean;
  requireTwoFactorForHost: boolean;
  codeLength: number;
}

const AccessCodeManagementPage: React.FC = () => {
  const theme = useTheme();
  const [accessCodes, setAccessCodes] = useState<AccessCode[]>([]);
  const [selectedCode, setSelectedCode] = useState<AccessCode | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [manualGenerateOpen, setManualGenerateOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'used' | 'expired' | 'revoked'>('all');

  const [codeSettings, setCodeSettings] = useState<CodeSettings>({
    generateBeforeMinutes: 60,
    expiryAfterMinutes: 240,
    maxVerificationAttempts: 3,
    enableAutoGeneration: true,
    requireTwoFactorForHost: true,
    codeLength: 6,
  });

  const [stats, setStats] = useState({
    activeCodes: 23,
    usedToday: 156,
    failedAttempts: 8,
    averageVerificationTime: '45s',
    securityIncidents: 2,
    successRate: 94.2,
  });

  useEffect(() => {
    fetchAccessCodes();
    const interval = setInterval(fetchAccessCodes, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAccessCodes = async () => {
    // Mock data - replace with actual API call
    const mockCodes: AccessCode[] = [
      {
        id: '1',
        code: '847392',
        type: 'vehicle_pickup',
        bookingId: 'bk_789',
        generatedFor: { id: 'r1', name: 'Sarah Johnson', role: 'renter', phone: '+234***4567' },
        relatedTo: { id: 'h1', name: 'Michael Host', role: 'host' },
        vehicleInfo: { make: 'Toyota', model: 'Camry', plate: 'ABC-123-XY' },
        status: 'active',
        generatedAt: '2024-01-20T13:00:00Z',
        expiresAt: '2024-01-20T17:00:00Z',
        verificationAttempts: 0,
        maxAttempts: 3,
        securityEvents: [
          { type: 'generated', timestamp: '2024-01-20T13:00:00Z', details: 'Auto-generated 1 hour before rental' }
        ],
      },
      {
        id: '2',
        code: '293847',
        type: 'driver_verification',
        bookingId: 'bk_456',
        generatedFor: { id: 'd1', name: 'James Smith', role: 'driver', phone: '+234***7890' },
        relatedTo: { id: 'r2', name: 'John Doe', role: 'renter' },
        vehicleInfo: { make: 'BMW', model: '5 Series', plate: 'XYZ-456-AB' },
        status: 'used',
        generatedAt: '2024-01-20T12:00:00Z',
        expiresAt: '2024-01-20T16:00:00Z',
        usedAt: '2024-01-20T12:15:00Z',
        verificationAttempts: 1,
        maxAttempts: 3,
        securityEvents: [
          { type: 'generated', timestamp: '2024-01-20T12:00:00Z', details: 'Auto-generated for driver-renter verification' },
          { type: 'used', timestamp: '2024-01-20T12:15:00Z', details: 'Successfully verified by renter' }
        ],
      },
      {
        id: '3',
        code: '564738',
        type: 'renter_verification',
        bookingId: 'bk_123',
        generatedFor: { id: 'r3', name: 'Emily Brown', role: 'renter', phone: '+234***1234' },
        relatedTo: { id: 'd2', name: 'Alex Driver', role: 'driver' },
        vehicleInfo: { make: 'Mercedes', model: 'C300', plate: 'DEF-789-CD' },
        status: 'expired',
        generatedAt: '2024-01-20T08:00:00Z',
        expiresAt: '2024-01-20T12:00:00Z',
        verificationAttempts: 2,
        maxAttempts: 3,
        securityEvents: [
          { type: 'generated', timestamp: '2024-01-20T08:00:00Z', details: 'Generated for renter identity verification' },
          { type: 'attempted', timestamp: '2024-01-20T09:30:00Z', details: 'First verification attempt failed' },
          { type: 'attempted', timestamp: '2024-01-20T10:15:00Z', details: 'Second verification attempt failed' },
        ],
      },
    ];
    
    setAccessCodes(mockCodes);
  };

  const handleRevokeCode = async (codeId: string) => {
    setAccessCodes(prev => 
      prev.map(code => 
        code.id === codeId 
          ? { 
              ...code, 
              status: 'revoked' as const,
              securityEvents: [
                ...code.securityEvents,
                { type: 'revoked', timestamp: new Date().toISOString(), details: 'Manually revoked by admin' }
              ]
            }
          : code
      )
    );
  };

  const handleManualGenerate = async (bookingId: string, type: AccessCode['type']) => {
    // Generate new code logic
    setManualGenerateOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'used': return 'primary';
      case 'expired': return 'warning';
      case 'revoked': return 'error';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'vehicle_pickup': return <CarIcon fontSize="small" />;
      case 'driver_verification': return <DriverIcon fontSize="small" />;
      case 'renter_verification': return <PersonIcon fontSize="small" />;
      default: return <KeyIcon fontSize="small" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'vehicle_pickup': return 'Vehicle Pickup';
      case 'driver_verification': return 'Driver Verification';
      case 'renter_verification': return 'Renter Verification';
      default: return type;
    }
  };

  const formatTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffInMinutes = Math.floor((expires.getTime() - now.getTime()) / 60000);
    
    if (diffInMinutes <= 0) return 'Expired';
    if (diffInMinutes < 60) return `${diffInMinutes}m left`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h left`;
    return `${Math.floor(diffInMinutes / 1440)}d left`;
  };

  const filteredCodes = accessCodes.filter(code => {
    if (statusFilter !== 'all' && code.status !== statusFilter) return false;
    if (searchQuery && !code.bookingId.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !code.generatedFor.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Verification Code Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor and manage verification codes for vehicle pickups and user authentication
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <KeyIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.activeCodes}</Typography>
              <Typography variant="caption" color="text.secondary">Active Codes</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <VerifiedIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.usedToday}</Typography>
              <Typography variant="caption" color="text.secondary">Used Today</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <WarningIcon color="warning" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.failedAttempts}</Typography>
              <Typography variant="caption" color="text.secondary">Failed Attempts</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccessTimeIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.averageVerificationTime}</Typography>
              <Typography variant="caption" color="text.secondary">Avg Time</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SecurityIcon color="error" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.securityIncidents}</Typography>
              <Typography variant="caption" color="text.secondary">Security Issues</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ShieldIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">{stats.successRate}%</Typography>
              <Typography variant="caption" color="text.secondary">Success Rate</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by booking ID or user name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ minWidth: 300 }}
          />
          
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            sx={{ minWidth: 120 }}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="used">Used</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </TextField>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAccessCodes}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            startIcon={<KeyIcon />}
            onClick={() => setManualGenerateOpen(true)}
          >
            Generate Code
          </Button>

          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
          >
            Settings
          </Button>
        </Box>
      </Paper>

      {/* Verification Codes Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code Details</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Generated For</TableCell>
              <TableCell>Related To</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell>Status & Progress</TableCell>
              <TableCell>Time Remaining</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCodes.map((code) => (
              <TableRow key={code.id}>
                <TableCell>
                  <Box>
                    <Typography variant="h6" fontFamily="monospace" color="primary">
                      {code.code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Booking: {code.bookingId}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Generated: {format(new Date(code.generatedAt), 'MMM d, HH:mm')}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTypeIcon(code.type)}
                    <Typography variant="body2">{getTypeLabel(code.type)}</Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {code.generatedFor.name}
                    </Typography>
                    <Chip 
                      label={code.generatedFor.role} 
                      size="small" 
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {code.generatedFor.phone}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  {code.relatedTo && (
                    <Box>
                      <Typography variant="body2">
                        {code.relatedTo.name}
                      </Typography>
                      <Chip 
                        label={code.relatedTo.role} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                  )}
                </TableCell>

                <TableCell>
                  {code.vehicleInfo && (
                    <Box>
                      <Typography variant="body2">
                        {code.vehicleInfo.make} {code.vehicleInfo.model}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {code.vehicleInfo.plate}
                      </Typography>
                    </Box>
                  )}
                </TableCell>

                <TableCell>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip 
                        label={code.status} 
                        color={getStatusColor(code.status) as any}
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(code.verificationAttempts / code.maxAttempts) * 100}
                        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                        color={code.verificationAttempts >= code.maxAttempts ? 'error' : 'primary'}
                      />
                      <Typography variant="caption">
                        {code.verificationAttempts}/{code.maxAttempts}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography 
                    variant="body2"
                    color={code.status === 'expired' ? 'error' : code.status === 'active' ? 'success' : 'text.secondary'}
                  >
                    {formatTimeRemaining(code.expiresAt)}
                  </Typography>
                  {code.usedAt && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Used: {format(new Date(code.usedAt), 'HH:mm')}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small"
                        onClick={() => {
                          setSelectedCode(code);
                          setDetailsOpen(true);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    
                    {code.status === 'active' && (
                      <Tooltip title="Revoke Code">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleRevokeCode(code.id)}
                        >
                          <BlockIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Verification Code Details - {selectedCode?.code}
        </DialogTitle>
        <DialogContent>
          {selectedCode && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Code Information</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Code" secondary={selectedCode.code} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Type" secondary={getTypeLabel(selectedCode.type)} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Status" secondary={selectedCode.status} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Booking ID" secondary={selectedCode.bookingId} />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Generated" 
                      secondary={format(new Date(selectedCode.generatedAt), 'PPpp')} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Expires" 
                      secondary={format(new Date(selectedCode.expiresAt), 'PPpp')} 
                    />
                  </ListItem>
                  {selectedCode.usedAt && (
                    <ListItem>
                      <ListItemText 
                        primary="Used At" 
                        secondary={format(new Date(selectedCode.usedAt), 'PPpp')} 
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Verification Progress</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Attempts" 
                      secondary={`${selectedCode.verificationAttempts} of ${selectedCode.maxAttempts}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Generated For" 
                      secondary={`${selectedCode.generatedFor.name} (${selectedCode.generatedFor.role})`} 
                    />
                  </ListItem>
                  {selectedCode.relatedTo && (
                    <ListItem>
                      <ListItemText 
                        primary="Related To" 
                        secondary={`${selectedCode.relatedTo.name} (${selectedCode.relatedTo.role})`} 
                      />
                    </ListItem>
                  )}
                  {selectedCode.vehicleInfo && (
                    <ListItem>
                      <ListItemText 
                        primary="Vehicle" 
                        secondary={`${selectedCode.vehicleInfo.make} ${selectedCode.vehicleInfo.model} (${selectedCode.vehicleInfo.plate})`} 
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Security Events</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Event</TableCell>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedCode.securityEvents.map((event, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Chip 
                              label={event.type} 
                              size="small"
                              color={event.type === 'used' ? 'success' : 
                                     event.type === 'failed' || event.type === 'revoked' ? 'error' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {format(new Date(event.timestamp), 'MMM d, HH:mm:ss')}
                          </TableCell>
                          <TableCell>{event.details}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          {selectedCode?.status === 'active' && (
            <Button 
              color="error" 
              onClick={() => {
                handleRevokeCode(selectedCode.id);
                setDetailsOpen(false);
              }}
            >
              Revoke Code
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verification Code Settings</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Generate Before (Minutes)"
                type="number"
                value={codeSettings.generateBeforeMinutes}
                onChange={(e) => setCodeSettings(prev => ({
                  ...prev,
                  generateBeforeMinutes: parseInt(e.target.value)
                }))}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry After (Minutes)"
                type="number"
                value={codeSettings.expiryAfterMinutes}
                onChange={(e) => setCodeSettings(prev => ({
                  ...prev,
                  expiryAfterMinutes: parseInt(e.target.value)
                }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Verification Attempts"
                type="number"
                value={codeSettings.maxVerificationAttempts}
                onChange={(e) => setCodeSettings(prev => ({
                  ...prev,
                  maxVerificationAttempts: parseInt(e.target.value)
                }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code Length"
                type="number"
                value={codeSettings.codeLength}
                onChange={(e) => setCodeSettings(prev => ({
                  ...prev,
                  codeLength: parseInt(e.target.value)
                }))}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={codeSettings.enableAutoGeneration}
                    onChange={(e) => setCodeSettings(prev => ({
                      ...prev,
                      enableAutoGeneration: e.target.checked
                    }))}
                  />
                }
                label="Enable Auto Generation"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={codeSettings.requireTwoFactorForHost}
                    onChange={(e) => setCodeSettings(prev => ({
                      ...prev,
                      requireTwoFactorForHost: e.target.checked
                    }))}
                  />
                }
                label="Require Two-Factor for Host Verification"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setSettingsOpen(false)}>
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Generate Dialog */}
      <Dialog open={manualGenerateOpen} onClose={() => setManualGenerateOpen(false)}>
        <DialogTitle>Generate Verification Code</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Booking ID"
                placeholder="Enter booking ID"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Code Type"
                defaultValue="vehicle_pickup"
              >
                <option value="vehicle_pickup">Vehicle Pickup</option>
                <option value="driver_verification">Driver Verification</option>
                <option value="renter_verification">Renter Verification</option>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualGenerateOpen(false)}>Cancel</Button>
          <Button variant="contained">Generate Code</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessCodeManagementPage;